// Centre pane — the embedded YouTube player for the selected video (replaces
// the poster CoverPane). Privacy-enhanced embed host; autoplays each new
// selection unless switched off in settings. Falls back to a placeholder for
// videos that are no longer available on YouTube and to an empty-state prompt
// when nothing is selected.
//
// Known limitation: once the user clicks into the iframe, keydown events go to
// the cross-origin frame and the 0–9 / arrow shortcuts stop until they click
// outside again. We can't intercept that — we detect it (window blur while the
// iframe is the active element) and show a hint.

import { useEffect, useMemo, useState } from "react";

import { Logo } from "../components/Logo";
import { loadYtAutoplay, type YtVideoDetail } from "./api";

import "./youtube.css";

export function PlayerPane({
  videoId,
  video,
}: {
  videoId: string | null;
  video: YtVideoDetail | undefined;
}) {
  const [frameFocused, setFrameFocused] = useState(false);
  // Read once on mount, like the panel-side preference: toggling it in
  // settings takes effect when this route remounts.
  const autoplay = useMemo(() => loadYtAutoplay(), []);

  useEffect(() => {
    function onBlur() {
      // Focus moves into the iframe asynchronously; check on the next tick.
      window.setTimeout(() => {
        setFrameFocused(document.activeElement?.tagName === "IFRAME");
      }, 0);
    }
    function onFocus() {
      setFrameFocused(false);
    }
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    setFrameFocused(false);
  }, [videoId]);

  if (!videoId) {
    return (
      <div className="player-pane player-pane--empty">
        <span className="player-pane__glyph" aria-hidden>
          <Logo size={96} />
        </span>
        <div className="player-pane__hint">select a video from the explorer</div>
      </div>
    );
  }

  if (video?.unavailable) {
    return (
      <div className="player-pane player-pane--empty">
        <span className="player-pane__glyph" aria-hidden>
          <Logo size={96} />
        </span>
        <div className="player-pane__hint">this video is no longer available on youtube</div>
        <a className="player-pane__link" href={video.url} target="_blank" rel="noopener noreferrer">
          open on youtube ↗
        </a>
      </div>
    );
  }

  // The iframe is keyed on videoId, so it remounts per selection and autoplay
  // fires for every video — including each step of rate mode. playsinline
  // keeps iOS from taking over the screen with the native fullscreen player.
  const params = new URLSearchParams({
    rel: "0",
    playsinline: "1",
    autoplay: autoplay ? "1" : "0",
  });
  const src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?${params}`;

  return (
    <div className="player-pane">
      <div className="player-pane__frame">
        <iframe
          key={videoId}
          src={src}
          title={video ? video.title : "youtube player"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
      {frameFocused && (
        <div className="player-pane__focus-hint" role="status">
          the player has keyboard focus — click outside it to use the 0–9 shortcuts
        </div>
      )}
    </div>
  );
}
